import { css, cx } from "@emotion/css";
import styled from "@emotion/styled";
import {
  LightMode,
  Pill,
  Variables,
  chevronDownIcon,
  chevronUpIcon,
} from "@repo/skapa";
import { type ReactElement, useRef, useState } from "react";

import { zIndex } from "../Variables";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { WidthAwareContainer } from "./WidthAwareContainer";

const FilterContainer = styled.div`
  position: relative;
`;

interface DropDownProps {
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  rightAlign?: boolean;
}

const DropDownContainer = styled.div<DropDownProps>`
  position: absolute;
  top: 50px;
  ${(props) => (props.rightAlign ? "right : 0;" : "")}
  padding: ${Variables.space200};
  min-width: 300px;
  min-height: ${(props) => props.minHeight};
  max-height: ${(props) => props.maxHeight ?? props.height};
  overflow-x: hidden;
  overflow-y: auto;
  z-index: ${zIndex.filterDropdown};
  border: ${Variables.thicknessThin} solid ${LightMode.colourNeutral2};
  background-color: ${LightMode.colourNeutral1};
  -webkit-box-shadow: 5px 5px ${Variables.space125} 3px
    ${LightMode.colourNeutral3};
  box-shadow: ${Variables.shadowSizeDefault};
  border-radius: ${Variables.radiusM};
`;

const Backdrop = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${zIndex.backdrop};
`;

interface FilterPillProps {
  id: string;
  label: string;
  onClick?: (...args: any[]) => any;
  selected?: boolean;
  dropDownItems?: ReactElement;
  minHeight?: string;
  maxHeight?: string;
  setWidth?: (width: number) => void;
  rightAlign?: boolean;
  hidden?: boolean;
}

export const FilterPill = ({
  id,
  label,
  selected,
  dropDownItems,
  minHeight,
  maxHeight,
  setWidth,
  rightAlign,
  hidden,
}: FilterPillProps) => {
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowDropDown(false));

  const pill = document.getElementById(id);
  const height = `calc(100vh - ${pill?.getBoundingClientRect().top}px - 60px)`;

  return (
    <WidthAwareContainer setWidth={(width) => setWidth?.(hidden ? 0 : width)}>
      {!hidden && (
        <FilterContainer id={id}>
          <Pill
            selected={selected}
            size="small"
            label={label}
            trailingIcon={showDropDown ? chevronUpIcon : chevronDownIcon}
            onClick={() => {
              if (dropDownItems) {
                setShowDropDown(!showDropDown);
              }
            }}
            className={cx(
              dropDownItems &&
                showDropDown &&
                css`
                  background: ${LightMode.colourInteractivePrimaryBgPressed};
                  color: ${LightMode.colourNeutral1};
                  &:hover {
                    background: ${LightMode.colourInteractivePrimaryBgPressed};
                  }
                `
            )}
          />
          {dropDownItems && showDropDown && (
            <>
              <DropDownContainer
                ref={ref}
                height={height}
                minHeight={minHeight}
                maxHeight={maxHeight}
                rightAlign={rightAlign}
              >
                {dropDownItems}
              </DropDownContainer>
              <Backdrop />
            </>
          )}
        </FilterContainer>
      )}
    </WidthAwareContainer>
  );
};
